/**
 * The one line the product says to itself: a toast, and the hook that raises
 * one.
 *
 * This lived inside App.tsx until there were two screens that mount outside
 * App and still have something to say. The landing page is the first —
 * `useAccount` reports a sign-in and a failed sign-in through a `ToastFn`, and
 * a page that handed it a no-op would be a page where signing in silently
 * either worked or did not.
 *
 * Nothing about it changed in the move except its address. It is here rather
 * than in `ui/index.tsx` because that module is the product's vocabulary of
 * marks — a brand, a button, a meter — and this is a piece of screen furniture
 * with state and a timer behind it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ease } from '../lib/motion';

export interface ToastFn {
  (text: string, kind?: 'info' | 'error'): void;
}

export interface ToastState {
  id: number;
  text: string;
  kind: 'info' | 'error';
}

/**
 * A `notify` to hand down, and the toast it raises.
 *
 * `notify` is stable for the life of the mount, which is load-bearing rather
 * than tidy: it is passed to `useAccount`, which keeps it in a ref and arms
 * listeners with it once. An identity that changed per render would re-arm
 * them on every toast.
 */
export function useToast(): { toast: ToastState | null; notify: ToastFn; clear(): void } {
  const [toast, setToast] = useState<ToastState | null>(null);
  const id = useRef(0);
  const notify = useCallback<ToastFn>((text, kind = 'info') => {
    id.current += 1;
    setToast({ id: id.current, text, kind });
  }, []);
  const clear = useCallback(() => setToast(null), []);
  return { toast, notify, clear };
}

export function Toast({ toast, onDone }: { toast: ToastState | null; onDone(): void }) {
  // Keyed on the toast's id, not the object or the callback: an unstable
  // `onDone` would clear and re-arm this timer on every parent render, which
  // is how a toast ends up never leaving.
  const id = toast?.id;
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    if (id === undefined) return;
    const t = window.setTimeout(() => done.current(), 3400);
    return () => window.clearTimeout(t);
  }, [id]);

  // The live region is mounted for the life of the screen and only its
  // contents change. A region that appears together with its text is generally
  // not announced, which would make every error silent to a screen reader.
  return (
    <div role="status" aria-live="polite" aria-atomic="true">
      <AnimatePresence>
        {toast && (
          <motion.div
            key={toast.id}
            className={toast.kind === 'error' ? 'toast error' : 'toast'}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={ease}
          >
            {toast.text}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
